import { Dispatch } from 'redux';

import { runningInRNWebView } from '@eika-infrastruktur/mobilbank-lib';
import { get, post, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { CreateSblConsentRequestWeb, SblConsentInfoWeb } from '../generated-types';
import { AppState } from '../reducers';
import { ContextReducer } from '../reducers/context';
import { LoaderReducer } from '../reducers/loader';
import { customFailed } from '../util/common';
import { errorNames } from '../util/mappings/errorName';
import parseUrlParams from '../util/urlParams';
import { openStep } from './steps/steps';
import { getRequestHeaders } from './tools';
import { actionTypes, AppActions } from './types';

const RedirectUrl = (): string => {
    if (runningInRNWebView) {
        return `${window.location.origin}/sbl-mobilbank-redirect`;
    }
    let baseUrl = window.location.href;
    const hashIndex = baseUrl.indexOf('#');
    if (hashIndex > -1) {
        baseUrl = baseUrl.substring(0, hashIndex);
    }
    return encodeURI(baseUrl);
};

type Action<T> = import('./types').Action<T>;

export interface CreateSblConsentStartedAction extends Action<typeof actionTypes.CREATE_SBL_CONSENT_STARTED> {
    type: typeof actionTypes.CREATE_SBL_CONSENT_STARTED;
}

export interface CreateSblConsentFulfilledAction extends Action<typeof actionTypes.CREATE_SBL_CONSENT_FULFILLED> {
    type: typeof actionTypes.CREATE_SBL_CONSENT_FULFILLED;
    payload: SblConsentInfoWeb;
}

export interface CreateSblConsentFailedAction extends Action<typeof actionTypes.CREATE_SBL_CONSENT_FAILED> {
    type: typeof actionTypes.CREATE_SBL_CONSENT_FAILED;
    payload: RequestExceptionWithResponse;
    silent?: boolean;
}

export interface FetchSblConsentStartedAction extends Action<typeof actionTypes.FETCH_SBL_CONSENT_STARTED> {
    type: typeof actionTypes.FETCH_SBL_CONSENT_STARTED;
}

export interface FetchSblConsentFulfilledAction extends Action<typeof actionTypes.FETCH_SBL_CONSENT_FULFILLED> {
    type: typeof actionTypes.FETCH_SBL_CONSENT_FULFILLED;
    payload: SblConsentInfoWeb | null;
}

export interface FetchSblConsentFailedAction extends Action<typeof actionTypes.FETCH_SBL_CONSENT_FAILED> {
    type: typeof actionTypes.FETCH_SBL_CONSENT_FAILED;
    payload: RequestExceptionWithResponse;
}

export type SblActions =
    | CreateSblConsentStartedAction
    | CreateSblConsentFailedAction
    | CreateSblConsentFulfilledAction
    | FetchSblConsentStartedAction
    | FetchSblConsentFulfilledAction
    | FetchSblConsentFailedAction;

const createSblConsentStarted = (): CreateSblConsentStartedAction => ({
    type: actionTypes.CREATE_SBL_CONSENT_STARTED,
});

const createSblConsentFailed = (e: RequestExceptionWithResponse, silent = false): CreateSblConsentFailedAction => ({
    type: actionTypes.CREATE_SBL_CONSENT_FAILED,
    payload: e,
    silent,
});

const createSblConsentFulfilled = (json: SblConsentInfoWeb): CreateSblConsentFulfilledAction => ({
    type: actionTypes.CREATE_SBL_CONSENT_FULFILLED,
    payload: json,
});

const fetchSblConsentStarted = (): FetchSblConsentStartedAction => ({
    type: actionTypes.FETCH_SBL_CONSENT_STARTED,
});

const fetchSblConsentFailed = (e: RequestExceptionWithResponse): FetchSblConsentFailedAction => ({
    type: actionTypes.FETCH_SBL_CONSENT_FAILED,
    payload: e,
});

const fetchSblConsentFulfilled = (json: SblConsentInfoWeb | null): FetchSblConsentFulfilledAction => ({
    type: actionTypes.FETCH_SBL_CONSENT_FULFILLED,
    payload: json,
});

export const shouldCreateNewConsent = (status: SblConsentInfoWeb['sblStatus'] | null | undefined): boolean =>
    status == null || status === 'REJECTED' || status === 'EXPIRED' || status === 'UNKNOWN';

export const getOrCreateSblConsent =
    () =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { loader, user, context, sbl } = getState();
        const { lastName } = user.context;

        if (loader.isCreatingSblConsent || !shouldCreateNewConsent(sbl.sblStatus)) {
            return;
        }

        // An SBL consent cannot be created without the customer's last name. If we are not currently
        // fetching the user context and still have no last name, fail with a terminal error instead of
        // leaving the SBL step loader spinning indefinitely. While the user context is still loading we
        // do nothing and let the step re-trigger creation once it is available.
        if (!lastName) {
            if (!loader.isFetchingUserContext) {
                dispatch(createSblConsentFailed(customFailed(errorNames.internalError)));
            }
            return;
        }

        try {
            const request: CreateSblConsentRequestWeb = {
                redirectUrl: RedirectUrl(),
                lastName,
            };

            dispatch(createSblConsentStarted());
            const response: SblConsentInfoWeb = await post(
                `${actionTypes.RESOURCE_PATH}/sbl/get-or-create`,
                request,
                getRequestHeaders(context.config.bankregnr),
            );

            dispatch(createSblConsentFulfilled(response));

            if (response.sblStatus === 'ACCEPTED') {
                dispatch(openStep(3));
            }
        } catch (e) {
            // Keep flow-specific handling local; avoid opening the global error page for get-or-create failures.
            dispatch(createSblConsentFailed(e, true));
        }
    };

export const getSblConsent = async (loader: LoaderReducer, context: ContextReducer, dispatch: Dispatch<AppActions>) => {
    if (!loader.isFetchingSblConsent && !loader.hasFetchedSblConsent) {
        try {
            dispatch(fetchSblConsentStarted());
            const response: SblConsentInfoWeb | null = await get(
                `${actionTypes.RESOURCE_PATH}/sbl/get`,
                getRequestHeaders(context.config.bankregnr),
            );

            const urlParams = parseUrlParams();

            dispatch(fetchSblConsentFulfilled(response));
            if (response?.sblStatus === 'ACCEPTED' && urlParams?.Status === 'OK') {
                dispatch(openStep(3));
            }
        } catch (e) {
            dispatch(fetchSblConsentFailed(e));
        }
    }
};

export const sblFailed =
    () =>
    async (dispatch: Dispatch<AppActions>): Promise<void> => {
        dispatch(createSblConsentFailed(customFailed(errorNames.internalError)));
    };
